import {
  Box,
  Heading,
  HStack,
  Select,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/authContext";
import { useUser } from "../../contexts/userContext";
import { dashboardContainer, glassCard, mutedText, sectionHeading } from "../../themes/design";
import { createAppTrpcClient } from "../../trpcClient";
import type { CalendarData } from "../../types/activities";
import { sportIcon } from "../../utils/activity-format";
import ActivityHeatmap from "./ActivityHeatmap";

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { getAccessTokenAsync } = useAuth();
  const clientRef = useRef<ReturnType<typeof createAppTrpcClient> | null>(null);
  if (clientRef.current === null) {
    clientRef.current = createAppTrpcClient(getAccessTokenAsync);
  }
  const client = clientRef.current;

  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<CalendarData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const userId = user?.id;

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setIsLoading(true);
    setIsError(false);
    setData(null);
    client.activities.calendarData
      .query({ year })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: unknown) => {
        console.error("Failed to fetch dashboard data:", err);
        if (!cancelled) {
          setData(null);
          setIsError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, year, client]);

  if (!user) return null;

  const yearOptions = data?.yearRange ?? [new Date().getFullYear()];

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          <Box {...glassCard}>
            <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
              {["s1", "s2", "s3", "s4"].map((id) => (
                <Skeleton key={id} h="48px" borderRadius="md" />
              ))}
            </SimpleGrid>
          </Box>
          <Box {...glassCard}>
            <Skeleton h="114px" w="100%" borderRadius="md" />
          </Box>
          <Box {...glassCard}>
            <Skeleton h="32px" w="100%" borderRadius="md" />
          </Box>
        </>
      );
    }

    if (isError) {
      return (
        <Text color="red.400" py={8}>
          {t("FetchError")}
        </Text>
      );
    }

    if (!data || data.yearRange.length === 0) {
      return (
        <Text {...mutedText} py={8}>
          {t("DashboardNoData")}
        </Text>
      );
    }

    const distanceKm = (data.totals.distance / 1000).toFixed(0);
    const durationHrs = String(Math.round(data.totals.duration / 3600));
    const elevationM = Math.round(data.totals.elevation).toLocaleString();

    return (
      <>
        {/* Stat cards */}
        <Box {...glassCard}>
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
            <VStack gap={0}>
              <Text {...mutedText} fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                {t("DashboardActivities")}
              </Text>
              <Text color="primary.100" fontWeight="600" fontSize="lg">
                {data.totals.count}
              </Text>
            </VStack>
            <VStack gap={0}>
              <Text {...mutedText} fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                {t("DashboardDistance")}
              </Text>
              <Text color="primary.100" fontWeight="600" fontSize="lg">
                {t("DashboardTotalDistance", { value: distanceKm })}
              </Text>
            </VStack>
            <VStack gap={0}>
              <Text {...mutedText} fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                {t("DashboardDuration")}
              </Text>
              <Text color="primary.100" fontWeight="600" fontSize="lg">
                {t("DashboardTotalDuration", { value: durationHrs })}
              </Text>
            </VStack>
            <VStack gap={0}>
              <Text {...mutedText} fontSize="xs" textTransform="uppercase" letterSpacing="0.05em">
                {t("DashboardElevation")}
              </Text>
              <Text color="primary.100" fontWeight="600" fontSize="lg">
                {t("DashboardTotalElevation", { value: elevationM })}
              </Text>
            </VStack>
          </SimpleGrid>
        </Box>

        {/* Heatmap */}
        <Box {...glassCard}>
          <ActivityHeatmap days={data.days} year={year} />
        </Box>

        {/* Sport breakdown */}
        {data.sportBreakdown.length > 0 && (
          <Box {...glassCard}>
            <Wrap spacing={4}>
              {data.sportBreakdown.map((item) => (
                <WrapItem key={item.sportType}>
                  <Text color="primary.200" fontSize="sm">
                    {sportIcon(item.sportType)} {item.sportType}: {item.count}
                  </Text>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}
      </>
    );
  };

  return (
    <Box {...dashboardContainer}>
      {/* Header with year selector — always visible */}
      <HStack justifyContent="space-between" alignItems="baseline">
        <Heading {...sectionHeading}>{t("DashboardPage")}</Heading>
        <Select
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          bg="whiteAlpha.50"
          borderColor="whiteAlpha.200"
          color="primary.200"
          size="sm"
          maxW="120px"
        >
          {yearOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </Select>
      </HStack>

      {renderContent()}
    </Box>
  );
};

export default DashboardPage;
